import { useState } from "react";
import { LayoutGrid, Rows3 } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  addComparable,
  removeComparable,
  updateComparableEvidence,
  type ComparableArtist,
  type ComparableCandidate,
} from "@/api/discover";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AddComparableDialog from "./AddComparableDialog";
import { collectionResultMessage, evidenceStatus, friendlyError, type EvidenceStatus } from "./discoverCopy";

/** `?artists_view=cards|table`, kept separate from the Venues tab's `?view=`. */
type ViewMode = "cards" | "table";
const parseView = (value: string | null): ViewMode => (value === "table" ? "table" : "cards");

type Props = {
  artistId: string;
  comparables: ComparableArtist[];
  loading: boolean;
  error: string | null;
  onChanged: () => Promise<void> | void;
  onViewVenues: (comparable: ComparableArtist) => void;
  onEvidenceUpdated: () => void;
};

const TONE_CLASSES: Record<EvidenceStatus["tone"], string> = {
  ok: "text-emerald-600 dark:text-emerald-300",
  muted: "text-muted-foreground",
  warn: "text-amber-600 dark:text-amber-300",
  error: "text-destructive",
  busy: "text-primary",
};

export default function ComparableArtistsPanel({
  artistId,
  comparables,
  loading,
  error,
  onChanged,
  onViewVenues,
  onEvidenceUpdated,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Record<number, { text: string; tone: EvidenceStatus["tone"] }>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleAdd = async (candidate: ComparableCandidate) => {
    setActionError(null);
    try {
      // Creates only the private comparable relationship; never collects evidence.
      await addComparable(
        candidate.kind === "public" ? { public_artist_id: candidate.id } : { resolved_artist_id: candidate.id },
        artistId,
      );
      setPickerOpen(false);
      await onChanged();
    } catch (err) {
      setActionError(friendlyError(err, "We couldn't add that artist. Please try again."));
    }
  };

  const handleRemove = async (comparable: ComparableArtist) => {
    setActionError(null);
    setBusyId(comparable.id);
    try {
      await removeComparable(comparable.id, artistId);
      await onChanged();
      onEvidenceUpdated();
    } catch (err) {
      setActionError(friendlyError(err, "We couldn't remove that artist. Please try again."));
    } finally {
      setBusyId(null);
    }
  };

  const handleUpdate = async (comparable: ComparableArtist) => {
    setActionError(null);
    setBusyId(comparable.id);
    setMessages((prev) => ({ ...prev, [comparable.id]: { text: "Updating…", tone: "busy" } }));
    try {
      const response = await updateComparableEvidence(comparable.id, artistId);
      setMessages((prev) => ({ ...prev, [comparable.id]: collectionResultMessage(response.result) }));
      await onChanged();
      if (response.result === "collected") onEvidenceUpdated();
    } catch (err) {
      setMessages((prev) => ({
        ...prev,
        [comparable.id]: { text: friendlyError(err, "Couldn't update right now"), tone: "error" },
      }));
    } finally {
      setBusyId(null);
    }
  };

  const view = parseView(searchParams.get("artists_view"));
  const changeView = (next: ViewMode) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("artists_view", next);
        return params;
      },
      { replace: true },
    );

  /** Freshness is only surfaced when it is a customer-relevant problem; routine "up to date" stays out of view. */
  const problemFor = (comparable: ComparableArtist) => {
    const message = messages[comparable.id];
    if (message) return message;
    const status = evidenceStatus(comparable);
    return status.tone === "ok" ? null : { text: status.label, tone: status.tone };
  };

  const summary = (comparable: ComparableArtist) => {
    const evidence = comparable.evidence;
    if (!evidence || evidence.shows === 0) return null;
    const parts = [
      `${evidence.shows} show${evidence.shows === 1 ? "" : "s"} analyzed`,
      `${evidence.venues} venue${evidence.venues === 1 ? "" : "s"}`,
    ];
    if (evidence.upcoming > 0) parts.push(`${evidence.upcoming} upcoming`);
    return parts.join(" · ");
  };

  const renderLinks = (pa: ComparableArtist["public_artist"]) => (
    <>
      {pa.spotify_id && (
        <a
          className="text-xs text-primary/80 hover:underline"
          href={`https://open.spotify.com/artist/${pa.spotify_id}`}
          target="_blank"
          rel="noreferrer"
          aria-label={`${pa.name} on Spotify`}
        >
          Spotify ↗
        </a>
      )}
      {pa.bandsintown_slug && (
        <a
          className="text-xs text-primary/80 hover:underline"
          href={`https://www.bandsintown.com/a/${pa.bandsintown_slug}`}
          target="_blank"
          rel="noreferrer"
          aria-label={`${pa.name} on Bandsintown`}
        >
          Bandsintown ↗
        </a>
      )}
    </>
  );

  const renderUpdate = (comparable: ComparableArtist) => {
    const status = evidenceStatus(comparable);
    const busy = busyId === comparable.id;
    if (status.action === "find") {
      return (
        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => void handleUpdate(comparable)} disabled={busy}>
          {busy ? "Updating…" : "Find their venues"}
        </Button>
      );
    }
    if (status.action === "refresh") {
      return (
        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => void handleUpdate(comparable)} disabled={busy}>
          {busy ? "Updating…" : "Refresh"}
        </Button>
      );
    }
    return null;
  };

  const renderRemove = (comparable: ComparableArtist) => (
    <button
      type="button"
      className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      onClick={() => void handleRemove(comparable)}
      disabled={busyId === comparable.id}
      aria-label={`Remove ${comparable.public_artist.name}`}
    >
      Remove
    </button>
  );

  const renderView = (comparable: ComparableArtist) =>
    comparable.public_artist.last_collected_at ? (
      <Button size="sm" className="h-7 px-3 text-xs" onClick={() => onViewVenues(comparable)}>
        View venues
      </Button>
    ) : null;

  return (
    <section className="space-y-3" aria-label="Comparable artists">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Your comparable artists</h2>
          <p className="text-sm text-muted-foreground">Artists whose live history ArtistAscend uses to find venues for you.</p>
        </div>
        <div className="flex items-center gap-2">
          {comparables.length > 0 && (
            <div className="inline-flex rounded-md border border-border p-0.5" role="group" aria-label="Display mode">
              {(
                [
                  ["cards", "Cards", LayoutGrid],
                  ["table", "Table", Rows3],
                ] as const
              ).map(([mode, label, Icon]) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={view === mode}
                  onClick={() => changeView(mode)}
                  className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    view === mode ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          )}
          <Button onClick={() => setPickerOpen(true)}>Add comparable artist</Button>
        </div>
      </div>

      {actionError && (
        <p role="alert" className="text-sm text-destructive">
          {actionError}
        </p>
      )}

      {loading && <p className="text-sm text-muted-foreground">Loading your comparable artists…</p>}
      {!loading && error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {!loading && !error && comparables.length === 0 && (
        <div className="aa-glass-card rounded-2xl p-6 text-center">
          <p className="font-medium text-foreground">No comparable artists yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">Add artists whose touring history is relevant to where you want to play.</p>
          <Button className="mt-4" onClick={() => setPickerOpen(true)}>
            Add comparable artist
          </Button>
        </div>
      )}

      {comparables.length > 0 && view === "cards" && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-2">
          {comparables.map((comparable) => {
            const pa = comparable.public_artist;
            const problem = problemFor(comparable);
            const stats = summary(comparable);
            return (
              <article key={comparable.id} className="aa-glass-card flex flex-col gap-1.5 rounded-xl p-3" aria-label={pa.name}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground" title={pa.name}>
                    {pa.name}
                  </h3>
                  {renderRemove(comparable)}
                </div>
                {stats && <p className="text-xs text-muted-foreground">{stats}</p>}
                {problem && (
                  <p className={`text-xs ${TONE_CLASSES[problem.tone]}`} data-testid={`status-${comparable.id}`}>
                    {problem.text}
                  </p>
                )}
                <div className="mt-auto flex flex-wrap items-center gap-2 pt-0.5">
                  {renderView(comparable)}
                  {renderUpdate(comparable)}
                  {renderLinks(pa)}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {comparables.length > 0 && view === "table" && (
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <Table aria-label="Comparable artists">
            <TableHeader>
              <TableRow>
                <TableHead>Artist</TableHead>
                <TableHead className="text-right">Shows analyzed</TableHead>
                <TableHead className="text-right">Venues</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparables.map((comparable) => {
                const pa = comparable.public_artist;
                const problem = problemFor(comparable);
                return (
                  <TableRow key={comparable.id} aria-label={pa.name}>
                    <TableCell>
                      <span className="block font-medium text-foreground">{pa.name}</span>
                      <span className="flex gap-2">{renderLinks(pa)}</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{comparable.evidence?.shows ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">{comparable.evidence?.venues ?? 0}</TableCell>
                    <TableCell className="text-xs">
                      {problem ? (
                        <span className={TONE_CLASSES[problem.tone]} data-testid={`status-${comparable.id}`}>
                          {problem.text}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1.5">
                        {renderView(comparable)}
                        {renderUpdate(comparable)}
                        {renderRemove(comparable)}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AddComparableDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        artistId={artistId}
        onAdd={handleAdd}
      />
    </section>
  );
}
